import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Check, Pencil, Plus, Power, RefreshCcw, Trash2, WandSparkles } from 'lucide-react';
import { toast } from 'react-toastify';
import { api, MacroAIDraft, MacroEvent } from '../lib_api';

type FormState = {
  title: string;
  eventType: string;
  currency: string;
  impactLevel: string;
  eventTime: string;
  notes: string;
  riskSummary: string;
  sourceType: string;
  sourceName: string;
  sourceReference: string;
  aiConfidence: string;
  aiNotes: string;
  needsReview: boolean;
  inferredEventTime: boolean;
  status: string;
  isActive: boolean;
};

type DraftViewModel = MacroAIDraft & { id: string; selected: boolean };
type SuggestionErrorKind = 'unauthenticated' | 'forbidden' | 'validation' | 'backend-unavailable';

const EVENT_TYPES = ['cpi', 'nfp', 'fomc', 'interest_rate', 'gdp', 'retail_sales', 'other'];
const EVENT_TIME_LOOKBACK_MS = 24 * 60 * 60 * 1000;

function isValidEventTime(value: string | null | undefined) {
  if (!value) return false;
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return false;
  const ts = dt.getTime();
  const now = Date.now();
  return ts >= (now - EVENT_TIME_LOOKBACK_MS) && ts <= (now + (90 * 24 * 60 * 60 * 1000));
}

function formatEventTime(value: string | null | undefined) {
  if (!isValidEventTime(value)) return 'Needs date';
  return new Date(String(value)).toLocaleString();
}

const presets: Array<{ label: string; key: string; value: Partial<FormState> }> = [
  {
    label: 'CPI',
    key: 'cpi',
    value: {
      title: 'US CPI',
      eventType: 'cpi',
      currency: 'USD',
      impactLevel: 'high',
      notes: 'Monthly inflation release.',
    },
  },
  {
    label: 'NFP',
    key: 'nfp',
    value: {
      title: 'US Non-Farm Payrolls',
      eventType: 'nfp',
      currency: 'USD',
      impactLevel: 'high',
      notes: 'Monthly labor market release.',
    },
  },
  {
    label: 'FOMC',
    key: 'fomc',
    value: {
      title: 'FOMC Statement',
      eventType: 'fomc',
      currency: 'USD',
      impactLevel: 'high',
      notes: 'Federal Reserve policy statement.',
    },
  },
  {
    label: 'Interest Rate',
    key: 'interest_rate',
    value: {
      title: 'Fed Interest Rate Decision',
      eventType: 'interest_rate',
      currency: 'USD',
      impactLevel: 'high',
      notes: 'Policy rate update.',
    },
  },
  {
    label: 'GDP',
    key: 'gdp',
    value: {
      title: 'US GDP',
      eventType: 'gdp',
      currency: 'USD',
      impactLevel: 'high',
      notes: 'Quarterly growth release.',
    },
  },
  {
    label: 'Retail Sales',
    key: 'retail_sales',
    value: {
      title: 'US Retail Sales',
      eventType: 'retail_sales',
      currency: 'USD',
      impactLevel: 'high',
      notes: 'Monthly retail activity release.',
    },
  },
];

const blankForm: FormState = {
  title: '',
  eventType: 'cpi',
  currency: 'USD',
  impactLevel: 'high',
  eventTime: '',
  notes: '',
  riskSummary: '',
  sourceType: 'manual',
  sourceName: 'manual-calendar',
  sourceReference: '',
  aiConfidence: '',
  aiNotes: '',
  needsReview: false,
  inferredEventTime: false,
  status: 'confirmed',
  isActive: true,
};

function draftToEventForm(draft: DraftViewModel, options?: { activate?: boolean }): FormState {
  const activate = !!options?.activate;
  return {
    title: draft.title,
    eventType: draft.eventType,
    currency: draft.currency,
    impactLevel: draft.impactLevel,
    eventTime: draft.eventTime ? new Date(draft.eventTime).toISOString().slice(0, 16) : '',
    notes: draft.notes || '',
    riskSummary: draft.riskSummary || '',
    sourceType: 'ai_assisted',
    sourceName: 'openai',
    sourceReference: draft.sourceReference || '',
    aiConfidence: draft.aiConfidence == null ? '' : String(draft.aiConfidence),
    aiNotes: draft.aiNotes || '',
    needsReview: !activate,
    inferredEventTime: draft.inferredEventTime,
    status: activate ? 'confirmed' : 'draft',
    isActive: activate,
  };
}

function asDraftVm(draft: MacroAIDraft): DraftViewModel {
  return {
    ...draft,
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    selected: false,
  };
}

function classifySuggestionError(error: any): { kind: SuggestionErrorKind; message: string } {
  const status = Number(error?.response?.status || 0);
  const backendMessage = String(
    error?.response?.data?.message || error?.response?.data?.error || '',
  ).trim();

  if (status === 401) {
    return {
      kind: 'unauthenticated',
      message: backendMessage || 'Your admin session expired. Please sign in again.',
    };
  }
  if (status === 403) {
    return {
      kind: 'forbidden',
      message: backendMessage || 'You are signed in but this account does not have admin access.',
    };
  }
  if (status >= 400 && status < 500) {
    return {
      kind: 'validation',
      message: backendMessage || 'The request is invalid. Review the inputs and try again.',
    };
  }
  return {
    kind: 'backend-unavailable',
    message: backendMessage || 'Backend is unavailable. Confirm API is running and retry.',
  };
}

export default function MacroCalendar() {
  const [mode, setMode] = useState<'add' | 'edit' | null>(null);
  const [editing, setEditing] = useState<MacroEvent | null>(null);
  const [form, setForm] = useState<FormState>(blankForm);
  const [view, setView] = useState<'upcoming' | 'all' | 'inactive'>('upcoming');
  const [impact, setImpact] = useState('all');
  const [eventType, setEventType] = useState('all');
  const [status, setStatus] = useState('all');
  const [currency, setCurrency] = useState('USD');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [drafts, setDrafts] = useState<DraftViewModel[]>([]);
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
  const [parseText, setParseText] = useState('');
  const [batchContext, setBatchContext] = useState('');
  const [lookaheadDays, setLookaheadDays] = useState(30);
  const [suggestionsError, setSuggestionsError] = useState<{ kind: SuggestionErrorKind; message: string } | null>(null);

  const qc = useQueryClient();

  const eventsQuery = useQuery({
    queryKey: ['admin', 'macro-events', view, impact, eventType, status, currency, from, to],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (view === 'upcoming') params.set('upcoming', 'true');
      if (view === 'inactive') params.set('active', 'false');
      if (impact !== 'all') params.set('impact', impact);
      if (status !== 'all') params.set('status', status);
      if (currency) params.set('currency', currency);
      if (from) params.set('from', `${from}T00:00:00.000Z`);
      if (to) params.set('to', `${to}T23:59:59.000Z`);
      const res = await api.get<{ events: MacroEvent[] }>(`/api/macro/events?${params.toString()}`);
      return res.data.events;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: { id?: string; body: FormState }) => {
      const nextEventTime = payload.body.eventTime ? new Date(payload.body.eventTime).toISOString() : null;
      const body = {
        ...payload.body,
        eventTime: nextEventTime,
        aiConfidence: payload.body.aiConfidence ? Number(payload.body.aiConfidence) : null,
      };
      if (payload.id) {
        return (await api.put(`/api/macro/events/${payload.id}`, body)).data;
      }
      return (await api.post('/api/macro/events', body)).data;
    },
    onSuccess: () => {
      toast.success('Macro event saved.');
      closeModal();
      qc.invalidateQueries({ queryKey: ['admin', 'macro-events'] });
      qc.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to save event'),
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.put(`/api/macro/events/${id}`, { isActive }),
    onSuccess: (_data, vars) => {
      toast.success(vars.isActive ? 'Event reactivated.' : 'Event deactivated.');
      qc.invalidateQueries({ queryKey: ['admin', 'macro-events'] });
      qc.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Action failed'),
  });

  const approveEventMutation = useMutation({
    mutationFn: async (id: string) => (await api.post(`/api/macro/events/${id}/approve`)).data,
    onSuccess: () => {
      toast.success('Event approved as confirmed.');
      qc.invalidateQueries({ queryKey: ['admin', 'macro-events'] });
      qc.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Approve failed'),
  });

  const rejectEventMutation = useMutation({
    mutationFn: async (id: string) => (await api.post(`/api/macro/events/${id}/reject`)).data,
    onSuccess: () => {
      toast.success('Event rejected.');
      qc.invalidateQueries({ queryKey: ['admin', 'macro-events'] });
      qc.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Reject failed'),
  });

  const runSuggestionsMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post('/api/macro/suggestions/run', {
          symbol: 'XAUUSD',
          currency: 'USD',
          lookaheadDays,
          requestContext: batchContext,
        })
      ).data,
    onSuccess: (res: any) => {
      setSuggestionsError(null);
      toast.success(`Suggestions refreshed: +${res.created || 0} new, ${res.updated || 0} updated.`);
      qc.invalidateQueries({ queryKey: ['admin', 'macro-events'] });
      qc.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
    onError: (error: any) => {
      const classified = classifySuggestionError(error);
      setSuggestionsError(classified);
      toast.error(classified.message);
    },
  });

  const aiPrefillMutation = useMutation({
    mutationFn: async ({ preset }: { preset: string }) =>
      (await api.post<{ draft: MacroAIDraft }>('/api/macro/ai/prefill', { preset })).data,
    onSuccess: ({ draft }) => {
      setDrafts((prev) => [asDraftVm(draft), ...prev]);
      toast.success('AI draft added. Review before approval.');
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'AI prefill failed'),
  });

  const aiParseMutation = useMutation({
    mutationFn: async () =>
      (await api.post<{ drafts: MacroAIDraft[] }>('/api/macro/ai/parse', { rawText: parseText }))
        .data,
    onSuccess: ({ drafts: parsedDrafts }) => {
      setDrafts((prev) => [...parsedDrafts.map(asDraftVm), ...prev]);
      toast.success(`AI parsed ${parsedDrafts.length} draft(s).`);
      setParseText('');
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'AI parse failed'),
  });

  const aiBatchMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ drafts: MacroAIDraft[] }>('/api/macro/ai/generate-batch', {
          symbol: 'XAUUSD',
          currency: 'USD',
          lookaheadDays,
          requestContext: batchContext,
        })
      ).data,
    onSuccess: ({ drafts: batchDrafts }) => {
      setDrafts((prev) => [...batchDrafts.map(asDraftVm), ...prev]);
      toast.success(`AI generated ${batchDrafts.length} review drafts.`);
    },
    onError: (error: any) =>
      toast.error(error?.response?.data?.error || 'AI batch generation failed'),
  });

  const events = useMemo(() => {
    const all = eventsQuery.data || [];
    const filteredByType = eventType === 'all' ? all : all.filter((event) => event.eventType === eventType);
    const upcomingSafe = view === 'upcoming'
      ? filteredByType.filter((event) => isValidEventTime(event.eventTime))
      : filteredByType;
    return [...upcomingSafe].sort((a, b) => {
      const aTime = isValidEventTime(a.eventTime) ? new Date(a.eventTime).getTime() : Number.MAX_SAFE_INTEGER;
      const bTime = isValidEventTime(b.eventTime) ? new Date(b.eventTime).getTime() : Number.MAX_SAFE_INTEGER;
      return aTime - bTime;
    });
  }, [eventsQuery.data, eventType, view]);

  const selectedDrafts = useMemo(() => drafts.filter((draft) => draft.selected), [drafts]);

  function openAdd(preset?: Partial<FormState>) {
    setEditing(null);
    setForm({ ...blankForm, ...preset });
    setMode('add');
  }

  function openEdit(event: MacroEvent) {
    setEditing(event);
    setForm({
      title: event.title,
      eventType: event.eventType,
      currency: event.currency,
      impactLevel: event.impactLevel,
      eventTime: event.eventTime ? new Date(event.eventTime).toISOString().slice(0, 16) : '',
      notes: event.notes || '',
      riskSummary: event.riskSummary || '',
      sourceType: event.sourceType || 'manual',
      sourceName: event.sourceName || 'manual-calendar',
      sourceReference: event.sourceReference || '',
      aiConfidence: event.aiConfidence == null ? '' : String(event.aiConfidence),
      aiNotes: event.aiNotes || '',
      needsReview: !!event.needsReview,
      inferredEventTime: !!event.inferredEventTime,
      status: event.status || 'confirmed',
      isActive: event.isActive,
    });
    setMode('edit');
  }

  function closeModal() {
    setMode(null);
    setEditing(null);
    setForm(blankForm);
  }

  function updateDraft(id: string, patch: Partial<DraftViewModel>) {
    setDrafts((prev) => prev.map((draft) => (draft.id === id ? { ...draft, ...patch } : draft)));
  }

  function removeDraft(id: string) {
    setDrafts((prev) => prev.filter((draft) => draft.id !== id));
    if (editingDraftId === id) setEditingDraftId(null);
  }

  async function approveDraft(draft: DraftViewModel, activate: boolean) {
    if (!draft.eventTime) {
      toast.error('Event time is required before approval. Edit draft first.');
      return;
    }
    await saveMutation.mutateAsync({ id: undefined, body: draftToEventForm(draft, { activate }) });
    removeDraft(draft.id);
  }

  async function approveSelected(activate: boolean) {
    if (!selectedDrafts.length) return;
    for (const draft of selectedDrafts) {
      // sequential to preserve toasts and avoid overwhelming backend
      // eslint-disable-next-line no-await-in-loop
      await approveDraft(draft, activate);
    }
  }

  return (
    <div className="space-y-4 overflow-x-hidden">
      <section className="panel p-3 sm:p-4">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-base font-semibold text-darkText dark:text-darkTextPrimary">
              Quick Presets
            </h3>
            <p className="text-sm text-mutedGray dark:text-darkTextSecondary">
              Prefill common high-impact USD events for fast monthly entry.
            </p>
          </div>
          <button className="btn" onClick={() => openAdd()} type="button">
            <Plus className="h-4 w-4" /> Add Event
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {presets.map((preset) => (
            <div key={preset.label} className="flex gap-2">
              <button type="button" className="chip" onClick={() => openAdd(preset.value)}>
                {preset.label}
              </button>
              <button
                type="button"
                className="chip"
                onClick={() => aiPrefillMutation.mutate({ preset: preset.key })}
                title={`AI Prefill ${preset.label}`}
              >
                <WandSparkles className="h-3 w-3" /> AI Prefill
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="panel space-y-3 p-3 sm:p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Bot className="h-4 w-4" />
          <h3 className="text-base font-semibold text-darkText dark:text-darkTextPrimary">
            AI Assist Drafting
          </h3>
          <button
            className="chip"
            type="button"
            onClick={() => !runSuggestionsMutation.isPending && runSuggestionsMutation.mutate()}
            disabled={runSuggestionsMutation.isPending}
          >
            {runSuggestionsMutation.isPending ? 'Refreshing…' : 'Run Background Suggestions'}
          </button>
        </div>
        {suggestionsError ? (
          <div className="rounded border border-red-300 bg-red-50 p-2 text-sm text-red-700">
            <div className="font-medium capitalize">{suggestionsError.kind.replace('-', ' ')}</div>
            <div>{suggestionsError.message}</div>
          </div>
        ) : null}
        <p className="text-sm text-mutedGray dark:text-darkTextSecondary">
          AI output is always <b>Draft</b>, <b>Needs Review</b>, and <b>Not Active</b> until
          explicitly approved.
        </p>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <div className="space-y-2">
            <label className="text-xs font-medium">AI Parse Text</label>
            <textarea
              className="input"
              rows={4}
              value={parseText}
              onChange={(e) => setParseText(e.target.value)}
              placeholder="Paste trusted calendar text to extract major USD events..."
            />
            <button
              className="btn"
              type="button"
              disabled={!parseText.trim() || aiParseMutation.isPending}
              onClick={() => aiParseMutation.mutate()}
            >
              {aiParseMutation.isPending ? 'Parsing…' : 'AI Parse Text'}
            </button>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium">AI Generate Monthly Drafts</label>
            <input
              className="input"
              value={batchContext}
              onChange={(e) => setBatchContext(e.target.value)}
              placeholder="Optional context (e.g., focus on next FOMC cycle)"
            />
            <input
              className="input"
              type="number"
              min={1}
              max={120}
              value={lookaheadDays}
              onChange={(e) => setLookaheadDays(Number(e.target.value || 30))}
            />
            <button
              className="btn"
              type="button"
              disabled={aiBatchMutation.isPending}
              onClick={() => aiBatchMutation.mutate()}
            >
              {aiBatchMutation.isPending ? 'Generating…' : 'AI Generate Drafts'}
            </button>
          </div>
        </div>
      </section>

      <section className="panel p-3 sm:p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-base font-semibold">AI Draft Review Queue ({drafts.length})</h3>
          <div className="flex flex-wrap gap-2">
            <button
              className="btn-outline"
              type="button"
              onClick={() => approveSelected(false)}
              disabled={!selectedDrafts.length || saveMutation.isPending}
            >
              Approve as Draft
            </button>
            <button
              className="btn"
              type="button"
              onClick={() => approveSelected(true)}
              disabled={!selectedDrafts.length || saveMutation.isPending}
            >
              Approve & Activate
            </button>
            <button
              className="chip"
              type="button"
              onClick={() => setDrafts([])}
              disabled={!drafts.length}
            >
              <Trash2 className="h-3 w-3" /> Reject All
            </button>
          </div>
        </div>
        {!drafts.length && <p className="text-sm text-mutedGray">No AI drafts yet.</p>}
        <div className="space-y-2">
          {drafts.map((draft) => {
            const editingDraft = editingDraftId === draft.id;
            return (
              <div
                key={draft.id}
                className="rounded border border-gray-200 p-3 dark:border-darkCard"
              >
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={draft.selected}
                      onChange={(e) => updateDraft(draft.id, { selected: e.target.checked })}
                    />
                    Select
                  </label>
                  <div className="flex flex-wrap gap-1 text-xs">
                    <span className="rounded bg-indigo-100 px-2 py-1 text-indigo-700">
                      AI Draft
                    </span>
                    <span className="rounded bg-amber-100 px-2 py-1 text-amber-700">
                      Needs Review
                    </span>
                    <span className="rounded bg-gray-200 px-2 py-1 text-gray-700">Not Active</span>
                    {draft.inferredEventTime && (
                      <span className="rounded bg-orange-100 px-2 py-1 text-orange-700">
                        Inferred Time
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                  <input
                    className="input"
                    value={draft.title}
                    disabled={!editingDraft}
                    onChange={(e) => updateDraft(draft.id, { title: e.target.value })}
                  />
                  <select
                    className="input"
                    value={draft.eventType}
                    disabled={!editingDraft}
                    onChange={(e) => updateDraft(draft.id, { eventType: e.target.value as any })}
                  >
                    {EVENT_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  <select
                    className="input"
                    value={draft.impactLevel}
                    disabled={!editingDraft}
                    onChange={(e) => updateDraft(draft.id, { impactLevel: e.target.value as any })}
                  >
                    <option value="high">high</option>
                    <option value="medium">medium</option>
                    <option value="low">low</option>
                  </select>
                  <input
                    className="input"
                    value={draft.currency}
                    disabled={!editingDraft}
                    onChange={(e) =>
                      updateDraft(draft.id, { currency: e.target.value.toUpperCase() })
                    }
                  />
                  <input
                    className="input"
                    type="datetime-local"
                    value={
                      draft.eventTime ? new Date(draft.eventTime).toISOString().slice(0, 16) : ''
                    }
                    disabled={!editingDraft}
                    onChange={(e) =>
                      updateDraft(draft.id, {
                        eventTime: e.target.value ? new Date(e.target.value).toISOString() : null,
                      })
                    }
                  />
                  <input
                    className="input"
                    value={draft.aiConfidence ?? ''}
                    disabled={!editingDraft}
                    onChange={(e) =>
                      updateDraft(draft.id, {
                        aiConfidence: e.target.value ? Number(e.target.value) : null,
                      })
                    }
                    placeholder="Confidence 0-1"
                  />
                </div>
                <textarea
                  className="input mt-2"
                  rows={2}
                  value={draft.notes || ''}
                  disabled={!editingDraft}
                  onChange={(e) => updateDraft(draft.id, { notes: e.target.value })}
                  placeholder="Notes"
                />
                <textarea
                  className="input mt-2"
                  rows={2}
                  value={draft.riskSummary || ''}
                  disabled={!editingDraft}
                  onChange={(e) => updateDraft(draft.id, { riskSummary: e.target.value })}
                  placeholder="Risk summary"
                />
                <p className="mt-1 text-xs text-mutedGray">AI Notes: {draft.aiNotes || '—'}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    className="chip"
                    type="button"
                    onClick={() => setEditingDraftId(editingDraft ? null : draft.id)}
                  >
                    <Pencil className="h-3 w-3" /> {editingDraft ? 'Done' : 'Edit'}
                  </button>
                  <button className="chip" type="button" onClick={() => approveDraft(draft, false)}>
                    <Check className="h-3 w-3" /> Approve as Draft
                  </button>
                  <button className="btn" type="button" onClick={() => approveDraft(draft, true)}>
                    Approve & Activate
                  </button>
                  <button className="chip" type="button" onClick={() => removeDraft(draft.id)}>
                    <Trash2 className="h-3 w-3" /> Reject
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="panel p-3 sm:p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <select className="input" value={view} onChange={(e) => setView(e.target.value as any)}>
            <option value="upcoming">Upcoming</option>
            <option value="all">All</option>
            <option value="inactive">Inactive</option>
          </select>
          <select className="input" value={impact} onChange={(e) => setImpact(e.target.value)}>
            <option value="all">All impact</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select
            className="input"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
          >
            <option value="all">All event types</option>
            <option value="cpi">CPI</option>
            <option value="nfp">NFP</option>
            <option value="fomc">FOMC</option>
            <option value="interest_rate">Interest Rate</option>
            <option value="gdp">GDP</option>
            <option value="retail_sales">Retail Sales</option>
            <option value="other">Other</option>
          </select>
          <select className="input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">All status</option>
            <option value="confirmed">Confirmed</option>
            <option value="suggested">Suggested</option>
            <option value="draft">Draft</option>
            <option value="rejected">Rejected</option>
          </select>
          <input
            className="input"
            value={currency}
            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            placeholder="Currency"
          />
          <input
            className="input"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
          <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </section>

      <section className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left dark:bg-white/5">
              <tr>
                {[
                  'Event Title',
                  'Event Type',
                  'Currency',
                  'Impact',
                  'Event Time',
                  'Source Type',
                  'Status',
                  'Notes',
                  'Actions',
                ].map((col) => (
                  <th
                    key={col}
                    className="px-3 py-2 font-medium text-mutedGray dark:text-darkTextSecondary"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id} className="border-t border-gray-100 dark:border-darkCard">
                  <td className="px-3 py-2 text-darkText dark:text-darkTextPrimary">
                    {event.title}
                  </td>
                  <td className="px-3 py-2">{event.eventType}</td>
                  <td className="px-3 py-2">{event.currency}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded px-2 py-1 text-xs ${event.impactLevel === 'high' ? 'bg-red-100 text-red-700' : event.impactLevel === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}`}
                    >
                      {event.impactLevel}
                    </span>
                  </td>
                  <td className="px-3 py-2" title={`raw event_time: ${String(event.eventTime ?? 'null')}`}>
                    {isValidEventTime(event.eventTime) ? (
                      formatEventTime(event.eventTime)
                    ) : (
                      <span className="rounded bg-amber-100 px-2 py-1 text-xs text-amber-700">Needs date</span>
                    )}
                  </td>
                  <td className="px-3 py-2">{event.sourceType}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      <span className={`rounded px-2 py-1 text-xs ${event.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' : event.status === 'suggested' ? 'bg-indigo-100 text-indigo-700' : event.status === 'rejected' ? 'bg-gray-200 text-gray-700' : 'bg-amber-100 text-amber-700'}`}>
                        {event.status || 'unknown'}
                      </span>
                      {event.needsReview ? <span className="rounded bg-amber-100 px-2 py-1 text-xs text-amber-700">Needs Review</span> : null}
                    </div>
                  </td>
                  <td className="max-w-[260px] truncate px-3 py-2" title={event.notes || ''}>
{event.notes || '—'}{event.aiConfidence != null ? ` (conf ${event.aiConfidence})` : ''}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-2">
                      <button className="chip" type="button" onClick={() => openEdit(event)}>
                        <Pencil className="h-3 w-3" />
                        Edit
                      </button>
                      {event.status !== 'confirmed' ? (
                        <button className="chip" type="button" onClick={() => approveEventMutation.mutate(event.id)}>
                          <Check className="h-3 w-3" />
                          Approve
                        </button>
                      ) : null}
                      {event.status !== 'rejected' ? (
                        <button className="chip" type="button" onClick={() => rejectEventMutation.mutate(event.id)}>
                          <Trash2 className="h-3 w-3" />
                          Reject
                        </button>
                      ) : null}
                      <button
                        className="chip"
                        type="button"
                        onClick={() => {
                          if (
                            confirm(`${event.isActive ? 'Deactivate' : 'Reactivate'} this event?`)
                          ) {
                            toggleMutation.mutate({ id: event.id, isActive: !event.isActive });
                          }
                        }}
                      >
                        {event.isActive ? (
                          <Power className="h-3 w-3" />
                        ) : (
                          <RefreshCcw className="h-3 w-3" />
                        )}{' '}
                        {event.isActive ? 'Deactivate' : 'Reactivate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!eventsQuery.isLoading && !events.length && (
          <div className="p-5 text-sm text-mutedGray dark:text-darkTextSecondary">
            No macro events match current filters.
          </div>
        )}
      </section>

      {mode && (
        <div className="modal-backdrop px-3 py-4">
          <form
            className="modal-content max-w-xl space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              saveMutation.mutate({ id: editing?.id, body: form });
            }}
          >
            <h3 className="text-lg font-semibold text-darkText dark:text-darkTextPrimary">
              {mode === 'add' ? 'Add Macro Event' : 'Edit Macro Event'}
            </h3>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <input
                className="input"
                placeholder="Title"
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                required
              />
              <select
                className="input"
                value={form.eventType}
                onChange={(e) => setForm((p) => ({ ...p, eventType: e.target.value }))}
              >
                <option value="cpi">CPI</option>
                <option value="nfp">NFP</option>
                <option value="fomc">FOMC</option>
                <option value="interest_rate">Interest Rate</option>
                <option value="gdp">GDP</option>
                <option value="retail_sales">Retail Sales</option>
                <option value="other">Other</option>
              </select>
              <input
                className="input"
                placeholder="Currency"
                value={form.currency}
                onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value.toUpperCase() }))}
                required
              />
              <select
                className="input"
                value={form.impactLevel}
                onChange={(e) => setForm((p) => ({ ...p, impactLevel: e.target.value }))}
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              <input
                className="input"
                type="datetime-local"
                value={form.eventTime}
                onChange={(e) => setForm((p) => ({ ...p, eventTime: e.target.value }))}
                required
              />
              <label className="flex items-center gap-2 text-sm text-mutedGray dark:text-darkTextSecondary">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                />{' '}
                Active
              </label>
              <input
                className="input"
                placeholder="Source Type (optional)"
                value={form.sourceType}
                onChange={(e) => setForm((p) => ({ ...p, sourceType: e.target.value }))}
              />
              <input
                className="input"
                placeholder="Source Name (optional)"
                value={form.sourceName}
                onChange={(e) => setForm((p) => ({ ...p, sourceName: e.target.value }))}
              />
            </div>
            <textarea
              className="input"
              placeholder="Notes"
              value={form.notes}
              onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
              rows={3}
            />
            <div className="flex justify-end gap-2">
              <button className="btn-outline" type="button" onClick={closeModal}>
                Cancel
              </button>
              <button className="btn" type="submit" disabled={saveMutation.isPending}>
                {saveMutation.isPending ? 'Saving…' : 'Save Event'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
